import { useState } from 'react';
import { useParams } from 'react-router-dom';
import classnames from 'classnames';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MdOutlineFileDownload } from 'react-icons/md';
import { ImQrcode } from 'react-icons/im';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Pagination } from '../../../components/Pagination';
import { Modal } from '../../../components/Modal';
import Loading from '../../../components/Loading';
import { Input } from '../../../components/Input';
import Button from '../../../components/Button/Button';
import { useSetFilters } from '../../../hooks/useSetFilters';
import { CustomSelect } from '../../../components/Shared/CustomSelect';
import { onIikoTransportImportTables } from '../../../store/Restaurant/actions';
import { useImportTables } from '../../../network';

export const Tables = () => {
    const { t } = useTranslation();
    const setFilters = useSetFilters();
    const { slug } = useParams();
    const queryClient = useQueryClient();
    const cn = classnames;

    const [isModalOpened, setIsModalOpened] = useState(false);
    const [qrCodeId, setQrCodeId] = useState(null);
    const [qrCodeUrl, setQrCodeUrl] = useState("");

    const { data: qcCodeImage = '', isLoading: isQcCodeLoading } = useQuery(
        ['qrCode', qrCodeId],
        () => axios.get(`/chain/tables/qrcode/download?rtid=${qrCodeId}`, { responseType: 'blob' }).then((res) => URL.createObjectURL(res.data)),
        { enabled: !!qrCodeId }
    );
    const { mutate: importTables, isLoading } = useImportTables()

    // get tables from react query
    const tables = queryClient.getQueryData(['tables', slug]) || {};
    const integrations = queryClient.getQueryData(['integrations', slug]) || {};

    const searchParams = new URLSearchParams(location.search);
    const currentPage = parseInt(searchParams.get('page')) || 1;
    const section = searchParams.get('section') || '';

    const numPages = Math.ceil(tables.total / tables.per_page || 1);

    const loadQrCode = (id, url) => {
        setQrCodeId(id);
        setIsModalOpened(true);
        setQrCodeUrl(url)
    };

    const handleCopy = () => {
        // copy to clipboard, it works only on https
        navigator.clipboard.writeText(qrCodeUrl);
    };

    const changeCurrentTablePage = (page) => {
        setFilters({ page });
    };

    const onChangeSection = ({ value }) => {
        setFilters({ section: value });
    };

    const sections = tables.data?.reduce((acc, curr) => {
        if (curr.restaurant_section) {
            acc[curr.restaurant_section.name] = curr;
        } else {
            acc['no-section'] = curr;
        }
        return acc;
    }, {});

    const sectionsOptions = [
        {
            label: t('All sections'),
            value: '',
        },
        ...Object.keys(sections || {}).map((key) => ({ label: key, value: key })),
    ];

    const filteredTables = section ? tables.data?.filter((table) => (table.restaurant_section ? table.restaurant_section.name === section : section === 'no-section')) : tables.data || [];

    const onImportTables = () => importTables({restaurant_id:slug, integration_id: integrations?.pos_selected?.integration_id})

    return (
        <div className=" rounded-lg shadow-md ">
            <div className="flex flex-col">
                <div className="flex justify-between m-4 mr-10">
                    <div className="px-3 py-3 relative">
                        {integrations?.pos_selected?.params?.terminal_group_id && (
                            <Button color="bg-yellow-400" isLoading={isLoading} disabled={!integrations?.pos_selected?.params?.terminal_group_id} type="button" onClick={onImportTables}>
                                {t('Import tables')}
                            </Button>
                        )}
                    </div>
                    {Object.keys(sections || {}).length > 0 && (
                        <CustomSelect options={sectionsOptions} defaultValue={sectionsOptions[0]} onChange={onChangeSection} placeholder={t('Section')} isSearchable={false} className="h-9 w-[190px] self-start" />
                    )}
                </div>
                <table className="min-w-full divide-gray-300">
                    <thead>
                        <tr>
                            <th scope="col" className="w-3/12 py-3 px-6 text-left text-sm font-normal text-gray-900 ">
                                {t('Table #')}
                            </th>
                            <th scope="col" className="py-3 px-3 text-left  text-sm font-normal text-gray-900 sm:table-cell">
                                {t('POS Table ID')}
                            </th>
                            <th scope="col" className="py-3 px-3 text-left text-sm font-normal text-gray-900 sm:table-cell">
                                {t('QR Code ID')}
                            </th>
                            <th scope="col" className="py-3 pl-3 pr-4 text-center text-sm font-normal text-gray-900 sm:pr-6 md:pr-0">
                                <span className="inline-block">
                                    <MdOutlineFileDownload size="25" />
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredTables.map((project, index) => (
                            <tr key={project.id} className={cn({ 'bg-gray-50': index % 2 === 0 })}>
                                <td className="py-6 px-6 text-sm">
                                    <div className="font-medium text-gray-900">{project.number || ''}</div>
                                </td>
                                <td className="py-6 px-3 text-sm text-gray-500 sm:table-cell">{project.external_uuid}</td>
                                <td className="py-6 px-3 text-sm text-gray-500 sm:table-cell">
                                    <ImQrcode size={35} className="cursor-pointer" onClick={() => loadQrCode(project.id, project.qrcode_url)} />
                                </td>
                                <td className="py-6 pl-3 pr-4 text-sm text-center text-gray-500">
                                    <a href={`/chain/tables/qrcode/download?rtid=${project.id}`}>Download</a>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="py-3 px-6 border-t border-gray-200">
                <Pagination numPages={numPages} currentPage={currentPage} setCurrentPage={changeCurrentTablePage} />
            </div>
            <Modal className="w-max" isOpen={isModalOpened} setIsOpen={setIsModalOpened}>
                <Loading loading={isQcCodeLoading}>
                    <div className="min-w-[100px] min-h-[100px]">
                        <img src={qcCodeImage} />
                        <div className="mt-5 max-w-[300px] grid grid-cols-3 gap-4">
                            <Input className="col-span-2" value={qrCodeUrl} />
                            <Button onClick={handleCopy}>Copy</Button>
                        </div>
                    </div>
                </Loading>
            </Modal>
        </div>
    );
};
